import { CheckoutContext } from '@/context/CheckoutContext';
import { useEffect, useRef, useState } from 'react';
import { useContext } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import { getStripePaymentKeyBySelectedPaymentMethod } from '@/utils/stripe';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { stripeService } from '@/services/stripe';
import { STRIPE_CONFIG } from '@/constants/stripe';
import { loadStripeResilient } from '@/utils/stripeLoader';
import { InfoLogs } from '@/constants/logs';
import { logWarn } from '@/utils/logger';

/**
 * Hook outputs:
 *   - `stripe`          : resolved `Stripe` instance (undefined while loading)
 *   - `isLoading`       : true until `loadStripe()` resolves
 *   - `stripePromise`   : the underlying `Promise<Stripe | null>`, available
 *                         synchronously on the first render. Pass this
 *                         directly to `<Elements stripe={...}>` so the
 *                         form's layout mounts on frame 1 (the bordered
 *                         field wrappers + Stripe's in-wrapper `loader:
 *                         'auto'` skeleton are visible immediately,
 *                         instead of being preceded by a centered spinner
 *                         that swaps to the full form ~200ms later). Stable
 *                         across re-renders (pinned via `useRef`) to match
 *                         the previous "initialise once" semantics.
 *
 * Loads through {@link loadStripeResilient} (reload Stripe.js when the eager
 * script failed/stalled). Always clears `isLoading` once the attempt settles —
 * including `null` / missing-key failures — so express wallet shells can leave
 * the placeholder spinner via their `init_error` path.
 */
export const useStripeInitialize = ({
  paymentMethod,
  isExpress,
}: {
  paymentMethod: PaymentMethodEnum;
  isExpress?: boolean;
}) => {
  const [stripe, setStripe] = useState<Stripe | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const { paymentKeys, paymentMethodsSettings } = useContext(CheckoutContext);

  // Build the Promise during render so callers can pass it to <Elements
  // stripe={...}> on the first paint. `useRef` (not `useMemo`) pins the
  // reference for the lifetime of the hook, matching the previous
  // `isStripeInitilized` guard semantics — once initialised, later
  // `paymentKeys` / `paymentMethodsSettings` changes are ignored.
  const stripePromiseRef = useRef<Promise<Stripe | null> | null | undefined>(undefined);
  if (stripePromiseRef.current === undefined) {
    const paymentKey = getStripePaymentKeyBySelectedPaymentMethod(
      paymentMethodsSettings,
      paymentMethod as ExcludeOtherPaymentMethod,
      paymentKeys
    );
    stripePromiseRef.current = paymentKey ? loadStripeResilient(paymentKey, STRIPE_CONFIG) : null;
  }
  const stripePromise = stripePromiseRef.current;

  // Latest-value ref so the failure log below can read `paymentMethod` WITHOUT
  // adding it to the effect deps — keeping the dependency array (and thus the
  // success path's re-run behaviour) byte-for-byte identical to the original.
  const paymentMethodRef = useRef(paymentMethod);
  paymentMethodRef.current = paymentMethod;

  useEffect(() => {
    // No publishable key → nothing will ever resolve. Clear loading so wallet
    // shells can collapse via their `init_error` path instead of spinning forever.
    if (!stripePromise) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    stripePromise.then((stripeInstance) => {
      if (cancelled) return;
      if (!stripeInstance) {
        // Stripe was unreachable after every reload. Clear loading so callers
        // (express wallet shells) can leave the placeholder spinner; RUM keeps
        // the payment-method label so the stuck-loading funnel stays sliceable.
        logWarn(InfoLogs.stripeInitUnavailable, null, {
          paymentMethod: paymentMethodRef.current,
          isExpress: Boolean(isExpress),
        });
        setIsLoading(false);
        return;
      }
      setStripe(stripeInstance);
      if (isExpress) {
        stripeService.initilizeStripeExpress(stripeInstance);
      } else {
        stripeService.initialize(stripeInstance);
      }
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [stripePromise, isExpress]);

  return { stripe, isLoading, stripePromise };
};
